/** The things a complete beginner needs before lesson one.
 *
 * The client asked for "basics like alphabets / numbers with sounds /
 * phonetics". This is the data; BasicsView renders it and every row is
 * spoken by our own German voice, so a learner hears a real pronunciation
 * rather than reading a respelling and guessing.
 *
 * `say` is what gets synthesised, which is NOT always what is shown. The
 * letter W is displayed as "W" but must be SPOKEN as its German name "Weh",
 * or the voice reads it as an English double-u. Same for the numbers, where
 * the digit is shown and the word is spoken.
 *
 * THE RESPELLING MUST BE GERMAN, NOT ENGLISH. It is read by a German
 * voice, which applies German spelling rules to whatever it is given -
 * so the respelling has to be something German orthography can produce.
 * C and Z were written "Tseh" and "Tsett", the way an English speaker
 * would transcribe the sound. German has no word-initial "ts": that
 * sound is spelled "z". The voice therefore read them as T-then-seh and
 * T-then-sett, and learners reported hearing "T C" and "T set" - which
 * is precisely what was written down for it. They are "Zeh" and "Zett",
 * both real German words, and the engine has always known how to say
 * them.
 *
 * The rule is pinned in basics.test.ts: no `say` value may contain a
 * letter sequence German spelling does not use.
 */

export interface BasicRow {
  /** What the learner sees — the letter, the digit, the spelling pattern. */
  show: string;
  /** What the voice is given. Often a respelling, so it is said correctly. */
  say: string;
  /** A short note in the learner's own language. */
  note?: string;
  /** A German word using it, for the sound in context. */
  example?: string;
  /** True when `say` is EXAMPLE WORDS rather than a pronunciation of what
   *  is shown.
   *
   *  It matters because the tile prints `say` under the big character as
   *  "here is how you say this". For a letter that is right - C / Zeh.
   *  For a sound pattern it is a lie: "ei" is not pronounced "mein Wein",
   *  those are two words that CONTAIN it. Learners tapped "ei" and were
   *  confused to hear words instead, which is a fair thing to be confused
   *  by when the screen labelled them as the pronunciation. */
  sayIsExample?: boolean;
}

/** The 26 letters with their German NAMES — how you say the letter when
 *  spelling your name down a phone line, which is where a learner meets
 *  them first. */
export const ALPHABET: BasicRow[] = [
  { show: "A", say: "Ah" }, { show: "B", say: "Beh" }, { show: "C", say: "Zeh" },
  { show: "D", say: "Deh" }, { show: "E", say: "Eh" }, { show: "F", say: "Eff" },
  { show: "G", say: "Geh" }, { show: "H", say: "Hah" }, { show: "I", say: "Ih" },
  { show: "J", say: "Jott" }, { show: "K", say: "Kah" }, { show: "L", say: "Ell" },
  { show: "M", say: "Emm" }, { show: "N", say: "Enn" }, { show: "O", say: "Oh" },
  { show: "P", say: "Peh" }, { show: "Q", say: "Kuh" }, { show: "R", say: "Err" },
  { show: "S", say: "Ess" }, { show: "T", say: "Teh" }, { show: "U", say: "Uh" },
  { show: "V", say: "Fau" }, { show: "W", say: "Weh" }, { show: "X", say: "Iks" },
  { show: "Y", say: "Ypsilon" }, { show: "Z", say: "Zett" },
];

/** The four letters English does not have. A learner who skips these cannot
 *  spell their own address in Germany. */
export const EXTRA_LETTERS: BasicRow[] = [
  { show: "Ä ä", say: "Ah Umlaut", example: "Mädchen" },
  { show: "Ö ö", say: "Oh Umlaut", example: "schön" },
  { show: "Ü ü", say: "Uh Umlaut", example: "über" },
  { show: "ß", say: "Eszett", example: "Straße" },
];

export const NUMBERS_0_20: BasicRow[] = [
  { show: "0", say: "null" }, { show: "1", say: "eins" }, { show: "2", say: "zwei" },
  { show: "3", say: "drei" }, { show: "4", say: "vier" }, { show: "5", say: "fünf" },
  { show: "6", say: "sechs" }, { show: "7", say: "sieben" }, { show: "8", say: "acht" },
  { show: "9", say: "neun" }, { show: "10", say: "zehn" }, { show: "11", say: "elf" },
  { show: "12", say: "zwölf" }, { show: "13", say: "dreizehn" },
  { show: "14", say: "vierzehn" }, { show: "15", say: "fünfzehn" },
  { show: "16", say: "sechzehn" }, { show: "17", say: "siebzehn" },
  { show: "18", say: "achtzehn" }, { show: "19", say: "neunzehn" },
  { show: "20", say: "zwanzig" },
];

export const TENS: BasicRow[] = [
  { show: "30", say: "dreißig" }, { show: "40", say: "vierzig" },
  { show: "50", say: "fünfzig" }, { show: "60", say: "sechzig" },
  { show: "70", say: "siebzig" }, { show: "80", say: "achtzig" },
  { show: "90", say: "neunzig" }, { show: "100", say: "hundert" },
  { show: "1000", say: "tausend" },
];

/** German says the units BEFORE the tens — 21 is "one-and-twenty". It is
 *  the single most common beginner mistake with numbers, and it matters in
 *  the Hoeren exam, where prices and times are the whole answer. */
export const NUMBER_ORDER: BasicRow[] = [
  { show: "21", say: "einundzwanzig" },
  { show: "34", say: "vierunddreißig" },
  { show: "67", say: "siebenundsechzig" },
  { show: "99", say: "neunundneunzig" },
];

/** Spelling patterns that do not sound the way an English reader expects.
 *  Each one is a mistake a beginner reliably makes. */
export const SOUNDS: BasicRow[] = [
  { show: "ei", say: "mein Wein", note: "sounds.ei", example: "mein, Wein, drei", sayIsExample: true },
  { show: "ie", say: "vier Liebe", note: "sounds.ie", example: "vier, Liebe, sieben", sayIsExample: true },
  { show: "eu / äu", say: "neun Häuser", note: "sounds.eu", example: "neun, Häuser", sayIsExample: true },
  { show: "ch", say: "ich nicht", note: "sounds.ch", example: "ich, nicht, München", sayIsExample: true },
  { show: "ach", say: "acht Buch", note: "sounds.ach", example: "acht, Buch, machen", sayIsExample: true },
  { show: "sch", say: "Schule schön", note: "sounds.sch", example: "Schule, schön", sayIsExample: true },
  { show: "sp / st", say: "Sport Straße", note: "sounds.sp", example: "Sport, Straße", sayIsExample: true },
  { show: "v", say: "Vater vier", note: "sounds.v", example: "Vater, vier, von", sayIsExample: true },
  { show: "w", say: "Wasser wir", note: "sounds.w", example: "Wasser, wir, wo", sayIsExample: true },
  { show: "z", say: "Zeit zwei", note: "sounds.z", example: "Zeit, zwei, Zug", sayIsExample: true },
  { show: "r", say: "rot Uhr", note: "sounds.r", example: "rot, Uhr, Bruder", sayIsExample: true },
  { show: "-er", say: "Bruder Vater", note: "sounds.er", example: "Bruder, Vater", sayIsExample: true },
];
